// src/hooks/useConfirmDialog.tsx
import { useState } from "react";
import { createPortal } from "react-dom";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { DialogTitle } from "@radix-ui/react-dialog";
import { Button } from "@/components/ui/button";

export function useConfirmDialog() {
  const [dialogData, setDialogData] = useState<null | {
    heading: string;
    statement: string;
    yesText?: string;
    noText?: string;
    resolve: (value: boolean) => void;
  }>(null);

  function confirm({
    heading,
    statement,
    yesText = "Yes",
    noText = "No",
  }: {
    heading: string;
    statement: string;
    yesText?: string;
    noText?: string;
  }): Promise<boolean> {
    return new Promise((resolve) => {
      setDialogData({ heading, statement, yesText, noText, resolve });
    });
  }

  // When user clicks Yes or No
  function handleResult(result: boolean) {
    if (dialogData?.resolve) dialogData.resolve(result);
    setDialogData(null); // Close dialog
  }

  // The popup UI (automatically mounted when dialogData is not null)
  const dialog = dialogData
    ? createPortal(
        <Dialog open={true} onOpenChange={() => handleResult(false)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{dialogData.heading}</DialogTitle>
            </DialogHeader>
            <p className="text-gray-600">{dialogData.statement}</p>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="ghost" onClick={() => handleResult(false)}>
                {dialogData.noText}
              </Button>
              <Button onClick={() => handleResult(true)}>
                {dialogData.yesText}
              </Button>
            </div>
          </DialogContent>
        </Dialog>,
        document.body
      )
    : null;

  return { confirm, dialog };
}
